/**
 * Minimal Swiper-compatible carousel engine.
 *
 * Replaces both the vendored swiper-bundle.min.js/css (CDN, ~158 KB) and
 * @splidejs/splide (CDN, only ever used for one fade carousel type) with a
 * single small engine. Exposes a global `Swiper` class with the same
 * constructor signature (`new Swiper(selectorOrEl, options)`) and the same
 * DOM classnames real Swiper used (.swiper-wrapper, .swiper-slide,
 * .swiper-pagination, .swiper-button-prev/next) so swiper-controls.js and
 * most templates' markup need no changes.
 *
 * Supported options (only what the theme actually uses):
 *   slidesPerView, slidesPerGroup, spaceBetween, speed, breakpoints, loop,
 *   watchOverflow, grabCursor, pagination: { el, type: 'progressbar' },
 *   navigation: { nextEl, prevEl }, effect: 'fade', fadeEffect.
 * observer/observeParents/resizeObserver/preloadImages are accepted but
 * covered by a single ResizeObserver on the root element.
 */
(function (window, document) {
    'use strict';

    var DRAG_THRESHOLD = 8;
    var SWIPE_DISTANCE_RATIO = 0.2;
    var SWIPE_VELOCITY = 0.3;

    function resolveEl(target) {
        if (!target) {
            return null;
        }
        return typeof target === 'string' ? document.querySelector(target) : target;
    }

    function Swiper(container, options) {
        this.el = resolveEl(container);
        if (!this.el) {
            return;
        }

        this.opts = options || {};
        this.wrapper = this.el.querySelector('.swiper-wrapper');
        this.slides = this.wrapper
            ? Array.prototype.filter.call(this.wrapper.children, function (child) {
                return child.classList.contains('swiper-slide');
            })
            : [];

        this.isFade = this.opts.effect === 'fade';
        this.spaceBetween = this.opts.spaceBetween || 0;
        this.speed = this.opts.speed != null ? this.opts.speed : 300;
        this.baseSlidesPerView = Number(this.opts.slidesPerView) || 1;
        this.baseSlidesPerGroup = Number(this.opts.slidesPerGroup) || this.baseSlidesPerView;
        this.breakpoints = this.opts.breakpoints || null;
        this.watchOverflow = !!this.opts.watchOverflow;
        this.loop = !!this.opts.loop;

        this.currentPage = 0;
        this.pageOffsets = [0];
        this.overflowing = false;

        this.navPrevEl = this.opts.navigation ? resolveEl(this.opts.navigation.prevEl) : null;
        this.navNextEl = this.opts.navigation ? resolveEl(this.opts.navigation.nextEl) : null;
        this.paginationEl = this.opts.pagination ? resolveEl(this.opts.pagination.el) : null;
        this.progressbarFillEl = null;

        this.el.style.setProperty('--swiper-speed', this.speed + 'ms');
        if (this.isFade) {
            this.el.classList.add('swiper-fade');
        }

        this._setupPagination();
        this._bindNavigation();
        this._bindDrag();

        var self = this;
        this._resizeObserver = new ResizeObserver(function () {
            self._layout(false);
        });
        this._resizeObserver.observe(this.el);

        this._layout(false);
    }

    Swiper.prototype._activeConfig = function () {
        var slidesPerView = this.baseSlidesPerView;
        var slidesPerGroup = this.baseSlidesPerGroup;

        if (this.breakpoints) {
            var width = window.innerWidth;
            Object.keys(this.breakpoints)
                .map(Number)
                .sort(function (a, b) { return a - b; })
                .forEach(function (breakpoint) {
                    if (width >= breakpoint) {
                        var config = this.breakpoints[breakpoint];
                        if (config.slidesPerView != null) {
                            slidesPerView = Number(config.slidesPerView) || slidesPerView;
                        }
                        if (config.slidesPerGroup != null) {
                            slidesPerGroup = Number(config.slidesPerGroup) || slidesPerGroup;
                        }
                    }
                }, this);
        }

        return { slidesPerView: slidesPerView, slidesPerGroup: slidesPerGroup };
    };

    Swiper.prototype._layout = function (animated) {
        if (!this.wrapper || !this.slides.length) {
            return;
        }

        if (this.isFade) {
            this.currentPage = Math.min(this.currentPage, this.slides.length - 1);
            this.pageOffsets = this.slides.map(function () { return 0; });
            this.overflowing = this.slides.length > 1;
            this._renderFade();
            this._updateNavigation();
            this._updatePagination();
            return;
        }

        var config = this._activeConfig();
        var slidesPerView = Math.min(config.slidesPerView, this.slides.length);
        var slidesPerGroup = Math.max(1, config.slidesPerGroup);
        var containerWidth = this.el.getBoundingClientRect().width;
        var slideWidth = (containerWidth - this.spaceBetween * (slidesPerView - 1)) / slidesPerView;

        this.slides.forEach(function (slide, index) {
            slide.style.width = slideWidth + 'px';
            slide.style.marginRight = index === this.slides.length - 1 ? '0px' : this.spaceBetween + 'px';
        }, this);

        var step = slideWidth + this.spaceBetween;
        var maxStartIndex = Math.max(0, this.slides.length - slidesPerView);
        var totalPages = Math.max(1, Math.ceil((this.slides.length - slidesPerView) / slidesPerGroup) + 1);

        this.pageOffsets = [];
        for (var page = 0; page < totalPages; page++) {
            var startIndex = Math.min(page * slidesPerGroup, maxStartIndex);
            this.pageOffsets.push(-(startIndex * step));
        }

        this.overflowing = this.slides.length > slidesPerView;
        this.currentPage = Math.min(this.currentPage, this.pageOffsets.length - 1);

        this._applyTranslate(animated);
        this._updateNavigation();
        this._updatePagination();
    };

    Swiper.prototype._applyTranslate = function (animated) {
        this.wrapper.style.transition = animated ? 'transform ' + this.speed + 'ms ease' : 'none';
        this.wrapper.style.transform = 'translate3d(' + this.pageOffsets[this.currentPage] + 'px,0,0)';
    };

    Swiper.prototype._renderFade = function () {
        this.slides.forEach(function (slide, index) {
            slide.classList.toggle('swiper-slide-active', index === this.currentPage);
        }, this);
    };

    Swiper.prototype._setupPagination = function () {
        if (!this.paginationEl || !this.opts.pagination || this.opts.pagination.type !== 'progressbar') {
            return;
        }

        this.paginationEl.classList.add('swiper-pagination-progressbar', 'swiper-pagination-horizontal');
        this.progressbarFillEl = this.paginationEl.querySelector('.swiper-pagination-progressbar-fill');
        if (!this.progressbarFillEl) {
            this.progressbarFillEl = document.createElement('span');
            this.progressbarFillEl.className = 'swiper-pagination-progressbar-fill';
            this.paginationEl.appendChild(this.progressbarFillEl);
        }
    };

    Swiper.prototype._updatePagination = function () {
        if (!this.paginationEl) {
            return;
        }

        var hide = this.watchOverflow && !this.overflowing;
        this.paginationEl.style.display = hide ? 'none' : '';

        if (this.progressbarFillEl) {
            var totalPages = this.pageOffsets.length;
            var progress = totalPages <= 1 ? 1 : this.currentPage / (totalPages - 1);
            this.progressbarFillEl.style.transform = 'scaleX(' + progress + ')';
        }
    };

    Swiper.prototype._bindNavigation = function () {
        var self = this;

        if (this.navPrevEl) {
            this.navPrevEl.addEventListener('click', function () { self.prev(); });
        }
        if (this.navNextEl) {
            this.navNextEl.addEventListener('click', function () { self.next(); });
        }
    };

    Swiper.prototype._updateNavigation = function () {
        var hideBoth = this.watchOverflow && !this.overflowing;
        var totalPages = this.pageOffsets.length;
        var atStart = !this.loop && this.currentPage === 0;
        var atEnd = !this.loop && this.currentPage === totalPages - 1;

        if (this.navPrevEl) {
            this.navPrevEl.style.display = hideBoth ? 'none' : '';
            this.navPrevEl.classList.toggle('swiper-button-disabled', hideBoth || atStart);
        }
        if (this.navNextEl) {
            this.navNextEl.style.display = hideBoth ? 'none' : '';
            this.navNextEl.classList.toggle('swiper-button-disabled', hideBoth || atEnd);
        }
    };

    Swiper.prototype._bindDrag = function () {
        if (this.isFade) {
            this._bindSwipeOnly();
            return;
        }

        if (!this.wrapper) {
            return;
        }

        var self = this;
        var activePointerId = null;
        var dragging = false;
        var startX = 0;
        var startTranslate = 0;
        var lastX = 0;
        var lastT = 0;
        var velocity = 0;
        var captured = false;

        function currentTranslate() {
            var matrix = window.getComputedStyle(self.wrapper).transform;
            if (!matrix || matrix === 'none') {
                return 0;
            }
            var match = matrix.match(/matrix\(([^)]+)\)/) || matrix.match(/matrix3d\(([^)]+)\)/);
            if (!match) {
                return 0;
            }
            var parts = match[1].split(',').map(parseFloat);
            return parts.length === 6 ? parts[4] : parts[12];
        }

        function clamp(value) {
            var min = Math.min.apply(null, self.pageOffsets);
            var max = Math.max.apply(null, self.pageOffsets);
            if (value > max) {
                return max + (value - max) * 0.3;
            }
            if (value < min) {
                return min + (value - min) * 0.3;
            }
            return value;
        }

        function onDown(event) {
            if (self.pageOffsets.length <= 1 || activePointerId !== null) {
                return;
            }
            activePointerId = event.pointerId;
            dragging = false;
            captured = false;
            startX = event.clientX;
            startTranslate = currentTranslate();
            lastX = startX;
            lastT = performance.now();
            velocity = 0;
        }

        function onMove(event) {
            if (event.pointerId !== activePointerId) {
                return;
            }
            var dx = event.clientX - startX;

            if (!dragging) {
                if (Math.abs(dx) < DRAG_THRESHOLD) {
                    return;
                }
                dragging = true;
                captured = true;
                self.wrapper.setPointerCapture(activePointerId);
                self.el.classList.add('swiper-dragging');
                self.wrapper.style.transition = 'none';
            }

            event.preventDefault();
            var now = performance.now();
            var dt = Math.max(1, now - lastT);
            velocity = (event.clientX - lastX) / dt;
            lastX = event.clientX;
            lastT = now;

            self.wrapper.style.transform = 'translate3d(' + clamp(startTranslate + dx) + 'px,0,0)';
        }

        function endDrag(event) {
            if (event.pointerId !== activePointerId) {
                return;
            }
            if (captured) {
                try {
                    self.wrapper.releasePointerCapture(activePointerId);
                } catch (e) {
                    // pointer already released (e.g. pointercancel) — ignore.
                }
            }
            activePointerId = null;
            self.el.classList.remove('swiper-dragging');

            if (!dragging) {
                return;
            }
            dragging = false;

            var dx = event.clientX - startX;
            var distanceThreshold = self.el.getBoundingClientRect().width * SWIPE_DISTANCE_RATIO;
            var direction = 0;
            if (Math.abs(velocity) > SWIPE_VELOCITY || Math.abs(dx) > distanceThreshold) {
                direction = dx < 0 ? 1 : -1;
            }

            if (direction !== 0) {
                self.goTo(self.currentPage + direction);
            } else {
                self._applyTranslate(true);
            }
        }

        this.wrapper.addEventListener('pointerdown', onDown);
        this.wrapper.addEventListener('pointermove', onMove);
        this.wrapper.addEventListener('pointerup', endDrag);
        this.wrapper.addEventListener('pointercancel', endDrag);
    };

    Swiper.prototype._bindSwipeOnly = function () {
        if (!this.wrapper) {
            return;
        }

        var self = this;
        var activePointerId = null;
        var startX = 0;
        var moved = false;

        function onDown(event) {
            if (activePointerId !== null) {
                return;
            }
            activePointerId = event.pointerId;
            startX = event.clientX;
            moved = false;
        }

        function onMove(event) {
            if (event.pointerId !== activePointerId) {
                return;
            }
            if (Math.abs(event.clientX - startX) >= DRAG_THRESHOLD) {
                moved = true;
            }
        }

        function onUp(event) {
            if (event.pointerId !== activePointerId) {
                return;
            }
            activePointerId = null;
            if (!moved) {
                return;
            }
            var dx = event.clientX - startX;
            if (Math.abs(dx) > 40) {
                self.goTo(self.currentPage + (dx < 0 ? 1 : -1));
            }
        }

        this.wrapper.addEventListener('pointerdown', onDown);
        this.wrapper.addEventListener('pointermove', onMove);
        this.wrapper.addEventListener('pointerup', onUp);
        this.wrapper.addEventListener('pointercancel', onUp);
    };

    Swiper.prototype.goTo = function (page) {
        var totalPages = this.pageOffsets.length;
        this.currentPage = this.loop
            ? ((page % totalPages) + totalPages) % totalPages
            : Math.max(0, Math.min(page, totalPages - 1));

        if (this.isFade) {
            this._renderFade();
        } else {
            this._applyTranslate(true);
        }

        this._updateNavigation();
        this._updatePagination();
    };

    Swiper.prototype.next = function () {
        this.goTo(this.currentPage + 1);
    };

    Swiper.prototype.prev = function () {
        this.goTo(this.currentPage - 1);
    };

    window.Swiper = Swiper;
})(window, document);
